import { useEffect, useRef, useState } from 'react';
import Sortable from 'sortablejs';
import { useEditor } from '../state/store';
import { useCode } from '../state/code-store';
import type { SectionGroup } from '../api/types';
import { AddSectionDialog } from './AddSectionDialog';
import { BlockList } from './BlockList';

/**
 * Left panel — mirrors Shopify's theme editor sidebar:
 *  - white background
 *  - expandable "Header / Template / Footer" groups
 *  - section rows with type icon + drag handle on hover
 *  - selection = blue tint + left accent bar
 */

function sectionLabel(type: string, schemas: Record<string, any>): string {
  const schema = schemas[type];
  if (schema?.name) return schema.name;
  return type.split('-').map((w) => w[0]?.toUpperCase() + w.slice(1)).join(' ');
}

/**
 * Section types that anchor the layout and can't be deleted through the UI.
 * Everything else — including announcement-bar and popup — is freely
 * removable. Keep in sync with any future schema flag like `locked: true`.
 */
const LOCKED_SECTION_TYPES = new Set(['header', 'footer']);

function isLockedSection(type: string, schema: any): boolean {
  if (LOCKED_SECTION_TYPES.has(type)) return true;
  if (schema?.locked === true) return true;
  return false;
}

// Shopify-style icon chip per section category
function sectionIcon(type: string) {
  const t = type.toLowerCase();
  const common = 'h-[14px] w-[14px]';
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round', strokeLinejoin: 'round' } as any;
  if (t.includes('header'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><path d="M3 6h18M3 12h18M3 18h18" /></svg>);
  if (t.includes('footer'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><rect x="3" y="14" width="18" height="6" rx="1" /><path d="M7 3v8M12 3v8M17 3v8" /></svg>);
  if (t.includes('slideshow') || t.includes('banner'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 9h18" /></svg>);
  if (t.includes('product'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><path d="M20 7 12 3 4 7m16 0-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>);
  if (t.includes('collection') || t.includes('featured'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>);
  if (t.includes('image') || t.includes('gallery'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></svg>);
  if (t.includes('text'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><path d="M4 7V5h16v2M9 5v14M15 19h-6" /></svg>);
  if (t.includes('newsletter') || t.includes('email'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>);
  if (t.includes('popup'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><rect x="5" y="5" width="14" height="14" rx="2" /><path d="M9 9h6v6H9z" /></svg>);
  if (t.includes('testimonial') || t.includes('review'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>);
  if (t.includes('contact') || t.includes('form'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 7h8M8 11h8M8 15h5" /></svg>);
  if (t.includes('faq') || t.includes('accordion'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 1 1 5.8 1c0 2-3 3-3 3M12 17h.01" /></svg>);
  if (t.includes('video'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><polygon points="5 3 19 12 5 21 5 3" /></svg>);
  if (t.includes('logo'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>);
  return (<svg viewBox="0 0 24 24" className={common} {...stroke}><rect x="3" y="3" width="18" height="18" rx="2" /></svg>);
}

// ---------------------------------------------------------------------------
// Group
// ---------------------------------------------------------------------------

interface GroupProps {
  title: string;
  group: SectionGroup;
  order: string[];
  sections: Record<string, any>;
  schemas: Record<string, any>;
  allowReorder?: boolean;
  allowAdd?: boolean;
  defaultOpen?: boolean;
}

interface SectionRowProps {
  sid: string;
  sec: any;
  group: SectionGroup;
  allowReorder?: boolean;
  allowAdd?: boolean;
  schemas: Record<string, any>;
  selection: ReturnType<typeof useEditor.getState>['selection'];
  select: ReturnType<typeof useEditor.getState>['select'];
  removeSection: ReturnType<typeof useEditor.getState>['removeSection'];
  toggleDisabled: ReturnType<typeof useEditor.getState>['toggleSectionDisabled'];
  openCodeForSection: (type: string) => Promise<void> | void;
}

function SectionRow({
  sid, sec, group, allowReorder, schemas,
  selection, select, removeSection, toggleDisabled, openCodeForSection,
}: SectionRowProps) {
  const schema = schemas[sec.type] || {};
  const blockSchemas = schema.blocks || [];
  const maxBlocks: number | undefined = typeof schema.max_blocks === 'number' ? schema.max_blocks : undefined;
  const hasBlocks = (sec.blockOrder || []).length > 0 || blockSchemas.length > 0;
  const canDelete = !isLockedSection(sec.type, schema);
  const canHide = !isLockedSection(sec.type, schema);

  const selected =
    !!selection &&
    (selection.kind === 'section' || selection.kind === 'block') &&
    selection.sectionId === sid &&
    selection.group === group;

  const hasBlockSelectedInside =
    !!selection &&
    selection.kind === 'block' &&
    selection.sectionId === sid &&
    selection.group === group;

  const [expanded, setExpanded] = useState(hasBlockSelectedInside);
  useEffect(() => {
    if (hasBlockSelectedInside) setExpanded(true);
  }, [hasBlockSelectedInside]);

  return (
    <li data-sid={sid} className="block">
      {/*
        Section + its expanded block list are wrapped in a SINGLE <li> so
        SortableJS moves them as one unit. Previously the block list was a
        sibling <li data-no-drag>, which meant dragging the section row
        only moved the header and stranded the block list — users saw the
        *next* section appear to move, not the one they grabbed.
      */}
      <div
        data-sid={sid}
        data-selected={selected && selection?.kind === 'section' ? 'true' : 'false'}
        className="lc-row group"
        onClick={() => select({ kind: 'section', group, sectionId: sid })}
      >
        {allowReorder && (
          <span
            data-drag-handle
            className="absolute left-1 cursor-grab text-icon-subdued opacity-0 group-hover:opacity-100"
            onClick={(e) => e.stopPropagation()}
            title="Arrastar para reordenar"
          >
            <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
              <circle cx="3" cy="3" r="1.1" /><circle cx="7" cy="3" r="1.1" />
              <circle cx="3" cy="7" r="1.1" /><circle cx="7" cy="7" r="1.1" />
              <circle cx="3" cy="11" r="1.1" /><circle cx="7" cy="11" r="1.1" />
            </svg>
          </span>
        )}

        {hasBlocks ? (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
            className="ml-2 flex h-4 w-4 shrink-0 items-center justify-center rounded text-icon-subdued hover:bg-surface-hovered hover:text-text"
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <svg
              width="10" height="10" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              className={'transition-transform ' + (expanded ? 'rotate-90' : '')}
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        ) : (
          <span className="ml-2 w-4" />
        )}

        <span className="text-icon-subdued">{sectionIcon(sec.type)}</span>
        <span className={'flex-1 truncate ' + (sec.disabled ? 'line-through opacity-60' : '')}>
          {sectionLabel(sec.type, schemas)}
        </span>

        <span className="ml-1 hidden gap-0.5 group-hover:flex">
          <IconBtn
            title="Editar código"
            onClick={(e) => { e.stopPropagation(); void openCodeForSection(sec.type); }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
          </IconBtn>
          {canHide && (
            <IconBtn
              title={sec.disabled ? 'Mostrar' : 'Ocultar'}
              onClick={(e) => { e.stopPropagation(); toggleDisabled({ group, sectionId: sid }); }}
            >
              {sec.disabled ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m3 3 18 18" />
                  <path d="M10.6 5.1A10 10 0 0 1 23 12a12.8 12.8 0 0 1-1.7 2.7" />
                  <path d="M6.6 6.6A13 13 0 0 0 1 12s4 8 11 8a10 10 0 0 0 4.8-1.2" />
                  <path d="M14 14.2a3 3 0 1 1-4.2-4.2" />
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </IconBtn>
          )}
          {canDelete && (
            <IconBtn
              title="Excluir"
              onClick={(e) => { e.stopPropagation(); removeSection({ group, sectionId: sid }); }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
              </svg>
            </IconBtn>
          )}
          {!canDelete && (
            <span
              className="flex h-6 w-6 items-center justify-center text-icon-subdued opacity-60"
              title="Esta seção é parte fixa do layout"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="11" width="14" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 1 1 8 0v4" />
              </svg>
            </span>
          )}
        </span>
      </div>

      {expanded && hasBlocks && (
        <div data-no-drag>
          <BlockList
            group={group}
            sectionId={sid}
            section={sec}
            blockSchemas={blockSchemas}
            maxBlocks={maxBlocks}
          />
        </div>
      )}
    </li>
  );
}

function Group({
  title, group, order, sections, schemas, allowReorder, allowAdd, defaultOpen = true,
}: GroupProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const reorderSections = useEditor((s) => s.reorderSections);
  const removeSection = useEditor((s) => s.removeSection);
  const select = useEditor((s) => s.select);
  const selection = useEditor((s) => s.selection);
  const toggleDisabled = useEditor((s) => s.toggleSectionDisabled);
  const setMode = useEditor((s) => s.setMode);
  const openFile = useCode((s) => s.openFile);
  const initCode = useCode((s) => s.init);
  const [addOpen, setAddOpen] = useState(false);
  const [open, setOpen] = useState(defaultOpen);

  const openCodeForSection = async (sectionType: string) => {
    const path = `sections/${sectionType}.liquid`;
    // LC-1577: inicializa o code-store ANTES de abrir arquivo. Sem
    // isso, `openFile` rodava com `session: null` (CodeMode ainda não
    // montou) e retornava early — primeiro clique caía numa aba vazia.
    const session = useEditor.getState().session;
    if (session) {
      const codeSession = useCode.getState().session;
      if (!codeSession || codeSession.tenantId !== session.tenantId) {
        await initCode(session);
      }
    }
    setMode('code');
    await openFile(path);
  };

  useEffect(() => {
    if (!allowReorder || !listRef.current || !open) return;
    const inst = Sortable.create(listRef.current, {
      animation: 160,
      handle: '[data-drag-handle]',
      filter: '[data-no-drag]',
      ghostClass: 'lc-drag-ghost',
      chosenClass: 'lc-drag-chosen',
      dragClass: 'lc-drag-drag',
      onEnd: (evt) => {
        if (evt.oldIndex === evt.newIndex) return;
        const ids = Array.from(listRef.current!.children)
          .map((li) => (li as HTMLElement).dataset.sid)
          .filter((v): v is string => !!v);
        if (ids.length) reorderSections(group, ids);
      },
    });
    return () => inst.destroy();
  }, [allowReorder, group, order.join(','), reorderSections, open]);

  return (
    <section className="mb-1">
      <button
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-subdued hover:text-text"
        onClick={() => setOpen((v) => !v)}
      >
        <svg
          width="10" height="10" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          className={'transition-transform ' + (open ? 'rotate-90' : '')}
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
        <span className="flex-1">{title}</span>
      </button>

      {open && (
        <ul ref={listRef} className="mb-1 space-y-px px-1.5">
          {order.map((sid) => {
            const sec = sections[sid];
            if (!sec) return null;
            return (
              <SectionRow
                key={sid}
                sid={sid}
                sec={sec}
                group={group}
                allowReorder={allowReorder}
                allowAdd={allowAdd}
                schemas={schemas}
                selection={selection}
                select={select}
                removeSection={removeSection}
                toggleDisabled={toggleDisabled}
                openCodeForSection={openCodeForSection}
              />
            );
          })}

          {allowAdd && (
            <li className="mt-1 px-1">
              <button
                onClick={() => setAddOpen(true)}
                className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] text-interactive hover:bg-interactive-subdued"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Adicionar seção
              </button>
            </li>
          )}
        </ul>
      )}

      {addOpen && <AddSectionDialog group={group} onClose={() => setAddOpen(false)} />}
    </section>
  );
}

function IconBtn({ title, onClick, children }: { title: string; onClick(e: React.MouseEvent): void; children: React.ReactNode }) {
  return (
    <button
      className="flex h-6 w-6 items-center justify-center rounded-md text-icon hover:bg-surface-hovered hover:text-text"
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

export function Sidebar() {
  const page = useEditor((s) => s.page);
  const order = useEditor((s) => s.order);
  const sections = useEditor((s) => s.sections);
  const headerGroup = useEditor((s) => s.headerGroup);
  const footerGroup = useEditor((s) => s.footerGroup);
  const cartDrawerGroup = useEditor((s) => s.cartDrawerGroup);
  const schemas = useEditor((s) => s.metadata?.theme.schemas || {});
  const select = useEditor((s) => s.select);
  const selection = useEditor((s) => s.selection);
  const cartPreviewMode = useEditor((s) => s.cartPreviewMode);
  // Drawer mode: lojista pediu "Carrinho (gaveta)" no dropdown.
  // Mostra o cart-drawer-group como grupo principal nesse modo.
  const isDrawerMode = page === 'cart' && cartPreviewMode === 'drawer';

  const pageLabels: Record<string, string> = {
    index: 'Página inicial',
    product: 'Produto',
    collection: 'Coleção',
    cart: 'Carrinho',
    page: 'Página',
    '404': '404',
  };

  return (
    <aside className="flex h-full w-[280px] shrink-0 flex-col border-r border-border bg-surface">
      {/* Section switcher: Sections / Theme settings — like Shopify tabs */}
      <div className="flex items-center gap-0.5 border-b border-border px-3 py-2">
        <button
          className={
            'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium transition-colors ' +
            (selection?.kind !== 'global'
              ? 'bg-surface-hovered text-text'
              : 'text-text-subdued hover:bg-surface-subdued hover:text-text')
          }
          onClick={() => select(null)}
          title="Seções"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="5" rx="1" />
            <rect x="3" y="11" width="18" height="10" rx="1" />
          </svg>
          Seções
        </button>
        <button
          className={
            'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium transition-colors ' +
            (selection?.kind === 'global'
              ? 'bg-surface-hovered text-text'
              : 'text-text-subdued hover:bg-surface-subdued hover:text-text')
          }
          onClick={() => select({ kind: 'global' })}
          title="Configurações do tema"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          Tema
        </button>
      </div>

      {/* Current page indicator */}
      <div className="flex items-center gap-2 border-b border-border-subdued px-3 py-2 text-[12px] text-text-subdued">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <path d="M9 22V12h6v10" />
        </svg>
        <span className="truncate">{pageLabels[page] || page}</span>
      </div>

      <div className="lc-scroll flex-1 overflow-y-auto py-2">
        {isDrawerMode && cartDrawerGroup ? (
          //
          // Drawer mode — esconde Cabeçalho/Modelo/Rodapé e mostra
          // SÓ o cart-drawer-group. O lojista vê só as sections do
          // drawer (configuração + qualquer custom que ele adicionou).
          //
          <Group
            title="Carrinho gaveta"
            group="cart-drawer-group"
            order={cartDrawerGroup.order}
            sections={cartDrawerGroup.sections}
            schemas={schemas}
            allowReorder
            allowAdd
          />
        ) : (
          <>
            {headerGroup && (
              <Group
                title="Cabeçalho"
                group="header-group"
                order={headerGroup.order}
                sections={headerGroup.sections}
                schemas={schemas}
                allowReorder
              />
            )}

            <Group
              title="Modelo"
              group="template"
              order={order}
              sections={sections}
              schemas={schemas}
              allowReorder
              allowAdd
            />

            {footerGroup && (
              <Group
                title="Rodapé"
                group="footer-group"
                order={footerGroup.order}
                sections={footerGroup.sections}
                schemas={schemas}
                allowReorder
              />
            )}
          </>
        )}
      </div>
    </aside>
  );
}
